<template>
    <full-screen-panel>
        <full-screen-panel-content>
            <fieldset class="w-full h-full" :disabled="selectedStep !== null">
                <div
                    class="flex-1 flex-grow basis-1 p-4 lg:p-8 overflow-visible relative max-w-[100vw] lg:min-h-0 overflow-y-auto">
                    <div class="text-center uppercase text-2xl font-bold tracking-wider mb-4">
                        Your Design
                    </div>
                    <div class="flex lg:flex-col overflow-auto space-y-1">
                        <menu-item
                            v-for="(productComponent, index) in productLayers"
                            :key="productComponent.placement.code"
                            type="color"
                            :title="productComponent.title"
                            :description="productComponent.selectedName"
                            :color="productComponent.component.custom.hex"
                            @selected="handleSelectStep('colors', productComponent)"
                            :tour-class="index === 0 ? 'cz__tour__colors': null"
                        ></menu-item>

                        <menu-item
                            :title="$t('GROUP_INFORMATION')"
                            type="group" @selected="handleSelectStep('group')"
                            v-if="isGroupOrder" />

                        <menu-item
                            :title="$t('MENU_IMAGES')"
                            type="images"
                            :tour-class="'cz__tour__images'"
                            @selected="handleSelectStep('images')"
                        />

                        <menu-item
                            :title="$t('MENU_TEXT')"
                            type="text"
                            :tour-class="'cz__tour__text'"
                            @selected="handleSelectStep('text')" />

                        <menu-item
                            title="Monogram"
                            type="monogram"
                            :tour-class="'cz__tour__monogram'"
                            @selected="handleSelectStep('monogram')"
                            v-if="!isGroupOrder"
                        />

                        <menu-item
                            :title="$t('GROUP_CUSTOMIZATION')"
                            type="group"
                            :tour-class="'cz__tour__group'"
                            @selected="showGroupConfirmationModal()"
                            v-if="!isGroupOrder" />

                        <menu-item
                            :title="$t('GROUP_INDIVIDUAL')"
                            type="individual" @selected="showIndividualConfirmationModal()"
                            v-if="isGroupOrder" />
                    </div>
                </div>
                <div class="w-full flex justify-center items-center p-2 my-4 max-w-xs mx-auto">
                    <number-selector
                        v-if="!isGroupOrder"
                        class="lg:hidden"
                        :searchable="false" :prefix="$t('QUANTITY_SELECTOR_SHORT')"
                        :value="quantity" :min="1"
                        :max="maxQuantity" @select="setQuantity($event)"
                        @decrease="setQuantity(quantity - 1)"
                        @increase="setQuantity(quantity + 1)" />
                    <primary-button
                        :tour-class="'cz__tour__done'"
                        @click.prevent="accept">
                        {{ actionCaption }}
                    </primary-button>
                </div>
                <div class="w-full flex flex-col justify-center items-center p-2 pb-8 mx-auto">
                    <div class="text-lg uppercase font-bold mb-2">
                        Please review your order
                    </div>
                    <div class="text-sm">
                        The design you see is what you'll see on your final product. Please review carefully.
                    </div>
                </div>
            </fieldset>
        </full-screen-panel-content>
    </full-screen-panel>
</template>

<style lang="scss">
.radio-button-active {
    .radio-button-wrapper {
        &:after {
            content: '';
            width: 9px;
            height: 9px;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: #000;
            border-radius: 50%;
        }
    }
}

.icon-individual {
    background: transparent url(../../../assets/order-single@2x.png) no-repeat center/contain;
}

.icon-group {
    background: transparent url(../../../assets/order-group@2x.png) no-repeat center/contain;
}
</style>

<script>
    import { mapActions, mapGetters, mapState } from 'vuex';
    import MenuItem from '../../MenuItem.vue';
    import NumberSelector from '../../NumberSelector.vue';
    import FullScreenPanel from '../FullScreenPanel.vue';
    import FullScreenPanelContent from '../FullScreenPanelContent.vue';
    import PrimaryButton from '../../buttons/PrimaryButton.vue';

    import * as constants from '../../../constants';

    export default {
        components: {
            MenuItem,
            NumberSelector,
            FullScreenPanel,
            FullScreenPanelContent,
            PrimaryButton,
        },
        data() {
            return {
                showErrors: false,
                selectedGroupOrder: false,
            };
        },
        name: 'MainMenuPanel',
        computed: {
            ...mapState({
                quantity: ({ quantity }) => quantity,
                isGroupOrder: ({ isGroupOrder }) => isGroupOrder,
                actionCaption: ({ actionCaption }) => actionCaption,
                viewController: ({ customizer }) => customizer.viewController,
                showMobileOrderTypePrompt: ({ showMobileOrderTypePrompt }) => showMobileOrderTypePrompt,
                selectedColorHex: ({ customizer }) => customizer.selectedColorHex,
                secondaryBottles: ({ customizer }) => customizer.secondaryBottles || [],
                selectedStep: ({ selectedStep }) => selectedStep,
                selectedDesignSide: ({ customizer }) => customizer.designSide,
                blueprint: ({ customizer }) => customizer.viewController.blueprint,
                options: ({ customizer }) => customizer.options,
            }),

            ...mapGetters({
                maxQuantity: 'availableQuantity',
                colorStepCaption: 'colorStepTitle',
                selectedColorHex: 'selectedColorHex',
                productLayers: 'productLayers',
            }),

            selectedColor() {
                const color = this.$store.getters.selectedColor;

                const method = this.$store.state.customizer.personalizationMethod;

                let methodName = '';

                if (method === constants.METHOD_PRINTING) {
                    methodName = this.$t('PRINTING_PRINTING');
                }

                if (method === constants.METHOD_ETCHING) {
                    methodName = this.$t('PRINTING_ETCHING');
                }

                return `${color} / ${methodName}`;
            },
            productTitle() {
                return (this.viewController && this.blueprint)
                    ? this.blueprint.name : '';
            },
            selectedDesignSideDescription() {
                const side = (this.selectedDesignSide || '').toUpperCase().replace('-', '');

                return this.$t(`DESIGN_SIDE_${side}_DESCRIPTION`);
            },
            /**
             * Calculates action caption depending on whether we are editing a recipe or a fresh new build.
             */
            actionCaption() {
                return (this.options.recipe && this.options.recipe !== 'null') ? this.$t('ACTION_UPDATE') : this.$t('ACTION_DONE');
            },
        },
        methods: {
            ...mapActions({
                closeMobileOrderTypePrompt: 'closeMobileOrderTypePrompt',
                selectStep: 'selectStep',
            }),
            /**
             * Prepares the design for adding to cart.
             */
            accept() {
                this.selectStep('review');
            },

            handleSelectStep(step, parameters) {
                if (this.isGroupOrder) {
                    // Switch to the primary bottle first.
                    if (this.secondaryBottles.indexOf(this.selectedBottle) !== 0) {
                        this.$store.dispatch('setSelectedBottle', this.secondaryBottles[0]).then(() => {
                            this.selectStep({ step, parameters });
                        });
                    } else {
                        this.selectStep({ step, parameters });
                    }
                } else {
                    this.selectStep({ step, parameters });
                }
            },

            showGroupConfirmationModal() {
                this.$store.dispatch('showGroupConfirmation', true);
            },
            showIndividualConfirmationModal() {
                this.$store.dispatch('showIndividualConfirmation', true);
            },
            /**
             * Set group order type
             */
            setIsGroupOrder(isGroupOrder) {
                this.selectedGroupOrder = isGroupOrder;
            },
            resetScrollPosition() {
                document.body.scrollTop = 0;
                document.documentElement.scrollTop = 0;
            },
            closeMobileOrderPrompt() {
                if (this.isGroupOrder !== this.selectedGroupOrder) {
                    this.$store.dispatch('setIsGroupOrder', this.selectedGroupOrder);
                }

                this.closeMobileOrderTypePrompt();

                this.$nextTick(() => this.resetScrollPosition());
            },
            setQuantity(v) {
                this.$store.dispatch('setQuantity', v);
            },
        },
    };
</script>
